// Considered: CollapsibleSectionAtom for the bands — rejected because Section B draws one bordered
// card with no disclosure affordance: four divider-separated rows that are always open, not
// accordion panels. See spec Reuse Audit.
// Visual-only phase — see spec acceptance test boardsTwoToSixParity for Phase 6 (handoff Section B).
import React from 'react';
import { useTranslation } from 'localization';
import StartFromBand from './bands/StartFromBand';
import NarrowedToBand from './bands/NarrowedToBand';
import AlwaysIncludeBand from './bands/AlwaysIncludeBand';
import ReadsAsBand from './bands/ReadsAsBand';
import type { ReadsAsExclusions } from './readsAsSentence';
import { RollupSetModel } from './rollupSetModel';
import type { PropertyOption, PropertyType } from '../PropertyFilter/PropertyFilterMolecule';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';
import type { RelationshipOption } from '../RelationshipFilter/RelationshipFilterMolecule';
import type { PerspectiveOption } from '../PerspectiveFilter/PerspectiveFilterMolecule';
import type { OrgAvailableTypeSchema } from 'types/schema';
import type { FilterGroupLeaf } from 'types/filters';
import type { RosterCoverage } from 'types/spherePoints';
import './RollupSetCardMolecule.scss';

export interface RollupSetCardMoleculeProps {
  value: RollupSetModel;
  onChange: (next: RollupSetModel) => void;
  /** The NARROWED TO tree's property catalog — without it the lane gate opens onto an empty picker. */
  availableProperties: PropertyOption[];
  excludePropertyTypes?: PropertyType[];
  arcTemplates?: ArcTemplateOption[];
  availableRelationships?: RelationshipOption[];
  familyTypeSchemas?: OrgAvailableTypeSchema[];
  availablePerspectives?: PerspectiveOption[];
  perspectiveSearchProps?: {
    schemaSystemName: string;
    sphereId: string;
    direction?: 'outwards' | 'inwards' | 'both';
    hops?: number;
  };
  /** The tree's "Add condition" catalog — the host's own six-lane `AspectCatalogMolecule`. */
  renderCatalog?: (opts: { onPick: (leaf: FilterGroupLeaf) => void; onClose: () => void }) => React.ReactNode;
  scopeTypeLabels?: string[];
  /** READS AS's server-computed roster coverage — the last-applied value, never recalculated here. */
  coverage?: RosterCoverage;
  /** True while local edits haven't been applied/saved — READS AS shows the last-applied count with an amber marker. */
  isDirty?: boolean;
  /** The roster's exclusions, so READS AS names them; absent when no roster is mounted. */
  excluded?: ReadsAsExclusions;
  disabled?: boolean;
  className?: string;
}

const RollupSetCardMolecule: React.FC<RollupSetCardMoleculeProps> = ({
  value,
  onChange,
  availableProperties,
  excludePropertyTypes,
  arcTemplates,
  availableRelationships,
  familyTypeSchemas,
  availablePerspectives,
  perspectiveSearchProps,
  renderCatalog,
  scopeTypeLabels,
  coverage,
  excluded,
  isDirty = false,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  return (
    <div className={`rollup-set-card ${className}`} data-testid="rollup-set-card">
      <span className="rollup-set-card__eyebrow" data-testid="rollup-set-card-eyebrow">
        {t('statusFactors.rollupSetEyebrow', 'one definition — the roster below is what it returns')}
      </span>
      <div className="rollup-set-card__band">
        <StartFromBand value={value} onChange={onChange} />
      </div>
      <div className="rollup-set-card__band">
        <NarrowedToBand
          value={value}
          onChange={onChange}
          availableProperties={availableProperties}
          excludePropertyTypes={excludePropertyTypes}
          arcTemplates={arcTemplates}
          availableRelationships={availableRelationships}
          familyTypeSchemas={familyTypeSchemas}
          availablePerspectives={availablePerspectives}
          perspectiveSearchProps={perspectiveSearchProps}
          renderCatalog={renderCatalog}
          scopeTypeLabels={scopeTypeLabels}
          disabled={disabled}
        />
      </div>
      <div className="rollup-set-card__band">
        <AlwaysIncludeBand value={value} onChange={onChange} disabled={disabled} />
      </div>
      <div className="rollup-set-card__band">
        <ReadsAsBand
          value={value}
          coverage={coverage}
          isDirty={isDirty}
          excluded={excluded}
          availableProperties={availableProperties}
          availableRelationships={availableRelationships}
          availablePerspectives={availablePerspectives}
        />
      </div>
    </div>
  );
};

RollupSetCardMolecule.displayName = 'RollupSetCardMolecule';

export default RollupSetCardMolecule;
